import {
    Column,
    Row,
    StatProps,
    Text,
    Icon,
    Image,
    useAppPalette,
    TextVariant,
    TextColor,
    MEDIA,
    useTextColorMap,
    StyledUnderline,
    StatColumn,
    IconType,
    Tooltip,
    CustomTooltipProps
} from "@bridgesplit/ui";
import {
    LoopscalePointsAction,
    useLoanMultipliersUtil,
    ParsedVaultRewardsScheduleExpanded,
    isRewardsActive,
    isSyntheticRewardsSchedule,
    BsMetaUtil,
    LendingVaultExpanded,
    convertExternalRewardToApy,
    getFirstPointsIntegration,
    getLendingVaultMints,
    getVaultLpVirtualPrice,
    useLatestCollateralYieldHistoryByMint,
    useOraclePrices,
    LoopExpanded,
    FarmingInfoExpanded
} from "@bridgesplit/abf-react";
import { LayersOutlined } from "@mui/icons-material";
import { combineCollections, filterTruthy, formatPercent, getUnixTs } from "@bridgesplit/utils";
import { TokenListMetadata } from "@bridgesplit/abf-sdk";
import { useMediaQuery } from "@mui/material";
import { TokenImage } from "app/components/common";

import { useRewardsColor } from "./util";
import { RewardsProtocolPoints } from "./RewardsProtocolPoints";

interface TooltipContentProps {
    children: React.ReactNode;
    width?: string;
    px?: number;
    py?: number;
    spacing?: number;
}
export function TooltipContent({
    children,
    width = "250px",
    px = 0,
    py = 0,
    spacing = 1
}: TooltipContentProps): JSX.Element {
    return (
        <Column spacing={spacing} py={py} px={px} sx={{ width }}>
            {children}
        </Column>
    );
}

export function CaptionWithIcon({
    caption,
    icon,
    variant = "body1",
    iconColor = "caption"
}: {
    caption: string;
    variant?: TextVariant;
    icon: React.ReactNode;
    iconColor?: string;
}) {
    return (
        <Row spacing={1}>
            <Text sx={{ color: iconColor }} variant="body1">
                {icon}
            </Text>
            <Text variant={variant} color="caption">
                {caption}
            </Text>
        </Row>
    );
}

export type ApyBreakdownProps = {
    label: string;
    icon: IconType;
    value: string | number | undefined;
}[];

export function ApyBreakdownTooltip({
    breakdown,
    ...props
}: { breakdown: ApyBreakdownProps } & Omit<CustomTooltipProps, "title">) {
    return (
        <Tooltip
            noPadding
            placement="bottom"
            {...props}
            title={
                <TooltipContent px={1} py={1}>
                    <ApyBreakdownTooltipContent breakdown={breakdown} />
                </TooltipContent>
            }
            reverseColors
        >
            {props.children}
        </Tooltip>
    );
}

export function ApyBreakdownTooltipContent({ breakdown }: { breakdown: ApyBreakdownProps }) {
    const { textSecondary } = useAppPalette();
    const ICON_SIZE = 15;

    return (
        <StatColumn
            stats={breakdown.map(({ label, icon, value }) => ({
                caption: (
                    <CaptionWithIcon
                        variant="body2"
                        icon={<Icon type={icon} sx={{ height: ICON_SIZE, width: ICON_SIZE }} />}
                        iconColor={textSecondary}
                        caption={label}
                    />
                ),
                value
            }))}
            loading={false}
            spacing={0.5}
            variant="body2"
        />
    );
}

export interface LendVaultAdditionalRewardsDisplayProps {
    vault?: LendingVaultExpanded;
    farmingInfo?: FarmingInfoExpanded;
}

const MAX_REWARD_IMAGES = 2;

export function LendVaultAdditionalRewardsDisplay({ vault, farmingInfo }: LendVaultAdditionalRewardsDisplayProps) {
    const vaultAddress = vault?.vault.address;
    const { loanMultipliers } = useLoanMultipliersUtil();
    const pointColor = useRewardsColor(loanMultipliers, vaultAddress ? [vaultAddress] : undefined, [
        LoopscalePointsAction.LiquidStake
    ]);

    const rewards = vault?.vaultRewardsSchedules?.filter(
        (reward) => isRewardsActive(reward) && !isSyntheticRewardsSchedule(reward)
    );
    const protocolPointsLogo = getFirstPointsIntegration(farmingInfo)?.partner?.partner.logoUrl;
    const imageUrls = [protocolPointsLogo, ...(rewards?.map((reward) => reward.rewardMetadata.imageUrl) ?? [])].filter(
        filterTruthy
    );
    // Cap images so the stack doesn't push adjacent text to a new line on narrow cards.
    const displayedImages = imageUrls.slice(0, MAX_REWARD_IMAGES);
    const rewardItems: RewardStackItem[] = [
        ...displayedImages.map((src, index) => ({ key: `image-${index}-${src}`, type: "image" as const, src })),
        {
            key: "loopscale-points",
            type: "points" as const,
            color: pointColor
        }
    ];

    return <Row spacing={0.5}>{!!rewardItems.length && <OverlappingRewardImages items={rewardItems} />}</Row>;
}

type RewardStackItem =
    | {
          key: string;
          type: "image";
          src: string;
      }
    | {
          key: string;
          type: "points";
          color: string;
      };

function OverlappingRewardImages({ items }: { items: RewardStackItem[] }) {
    const { paperBackground } = useAppPalette();

    return (
        <Row spacing={0}>
            {items.map((item, index) =>
                item.type === "image" ? (
                    <Image
                        key={item.key}
                        src={item.src}
                        size="16px"
                        variant="circle"
                        sx={{
                            ml: index === 0 ? 0 : -1,
                            zIndex: items.length - index,
                            border: `1px solid ${paperBackground}`
                        }}
                    />
                ) : (
                    <Text
                        key={item.key}
                        sx={{
                            ml: index === 0 ? 0 : -1,
                            zIndex: items.length - index,
                            width: "16px",
                            minWidth: "16px",
                            height: "16px",
                            minHeight: "16px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: item.color
                        }}
                    >
                        <Icon type="points" />
                    </Text>
                )
            )}
        </Row>
    );
}

export interface LoopsAdditionalRewardsDisplayProps {
    loopExpanded?: LoopExpanded;
    farmingInfo?: FarmingInfoExpanded;
}

export function LoopsAdditionalRewardsDisplay({ loopExpanded, farmingInfo }: LoopsAdditionalRewardsDisplayProps) {
    const loopAddress = loopExpanded?.vaultIdentifier;

    return (
        <Row spacing={0.5}>
            <RewardsProtocolPoints farmingInfo={farmingInfo} displayType="badge" />
            <PointDisplay
                address={loopAddress}
                loopscalePointsAction={LoopscalePointsAction.Borrow}
                collateralMultiplier={loopExpanded?.collateralToken}
            />
        </Row>
    );
}

// Address in points display is the address of the vault or loop, principal or collateral for borrow / lend
export function PointDisplay({
    address,
    loopscalePointsAction,
    collateralMultiplier
}: {
    address: string | undefined;
    loopscalePointsAction: LoopscalePointsAction;
    collateralMultiplier?: TokenListMetadata; //Only used for Loops to show collateral boost
}) {
    const { loanMultipliers } = useLoanMultipliersUtil();
    const color = useRewardsColor(loanMultipliers, [address, collateralMultiplier?.mint].filter(filterTruthy), [
        loopscalePointsAction
    ]);

    return (
        <Text sx={{ color }}>
            <Icon type="points" />
        </Text>
    );
}

export function RateDisplay({
    apy,
    variant = "body1",
    color = "body",
    isFeatured,
    underline = true,
    customDecimals = 2,
    maxPrecisionUi = 0.1
}: {
    apy: number | undefined;
    variant?: TextVariant;
    color?: TextColor;
    isFeatured?: boolean;
    underline?: boolean;
    customDecimals?: number;
    maxPrecisionUi?: number;
}) {
    const fixedWidth = "5ch";
    const isMobile = useMediaQuery(MEDIA.LG.below);

    if (isFeatured || isMobile) {
        return (
            <Row>
                <Text svgColor="disabled" variant={variant} color={color}>
                    <StyledUnderline underline={underline}>
                        {formatPercent(apy, { customDecimals, maxPrecisionUi, formatUndefinedAsNA: true })}
                    </StyledUnderline>
                </Text>
            </Row>
        );
    }

    return (
        <Row sx={{ minWidth: "max-content" }}>
            <Text
                svgColor="disabled"
                variant={variant}
                color={color}
                sx={{ display: "inline", width: fixedWidth, justifyContent: "right" }}
            >
                <StyledUnderline underline={underline}>
                    {formatPercent(apy, { customDecimals, maxPrecisionUi, formatUndefinedAsNA: true })}
                </StyledUnderline>
            </Text>
        </Row>
    );
}

//Lend Vault Additional Stats
export function useLendVaultAdditionalStats(lendingVaultExpanded: LendingVaultExpanded | undefined): StatProps[] {
    const collateralYieldStats = useCollateralYieldStats(lendingVaultExpanded);
    const externalRewardStats = useExternalRewardStats({ lendingVaultExpanded });
    return combineCollections([collateralYieldStats, externalRewardStats]);
}

export function useCollateralYieldStats(lendingVaultExpanded: LendingVaultExpanded | undefined): StatProps[] {
    const principalMint = lendingVaultExpanded?.vault.principalMint;
    const { data: collateralYield } = useLatestCollateralYieldHistoryByMint(principalMint ? [principalMint] : []);

    const textColorMap = useTextColorMap();
    if (!collateralYield || !lendingVaultExpanded || !principalMint || !collateralYield[principalMint]) return [];
    const symbol = BsMetaUtil.getSymbol(lendingVaultExpanded?.principalMetadata);

    return [
        {
            value: <Text variant="body2">{formatPercent(collateralYield[principalMint])}</Text>,
            caption: (
                <CaptionWithIcon
                    variant="body2"
                    caption={`${symbol} APY`}
                    iconColor={textColorMap("caption")}
                    icon={<LayersOutlined />}
                />
            )
        }
    ];
}

export function useExternalRewardStats({
    lendingVaultExpanded,
    depositAmount = 0
}: {
    lendingVaultExpanded: LendingVaultExpanded | undefined;
    depositAmount?: number;
}): StatProps[] {
    const rewardMintsWithPrincipal = getLendingVaultMints(lendingVaultExpanded);
    const { getUsdPrice } = useOraclePrices(rewardMintsWithPrincipal);
    if (!lendingVaultExpanded) return [];
    const now = getUnixTs();
    const lpPrice = getVaultLpVirtualPrice(lendingVaultExpanded, now);
    const principalPrice = getUsdPrice(lendingVaultExpanded?.vault.principalMint);
    const lpSupply = lendingVaultExpanded.vault.lpSupply;
    const activeRewards = lendingVaultExpanded.vaultRewardsSchedules.filter((schedule) => isRewardsActive(schedule));
    return activeRewards.map((schedule) =>
        createExternalRewardStat(
            schedule,
            lpPrice,
            principalPrice,
            lpSupply,
            getUsdPrice(schedule.rewardMetadata.mint) ?? 0,
            depositAmount
        )
    );
}

function createExternalRewardStat(
    schedule: ParsedVaultRewardsScheduleExpanded,
    lpPrice: number | undefined,
    principalPrice: number | undefined,
    lpSupply: number,
    rewardTokenPrice: number,
    depositAmount = 0
): StatProps {
    const apy = convertExternalRewardToApy(
        schedule,
        lpPrice,
        principalPrice,
        lpSupply,
        rewardTokenPrice,
        depositAmount
    );
    const symbol = BsMetaUtil.getSymbol(schedule.rewardMetadata);
    const caption = isSyntheticRewardsSchedule(schedule) ? `${symbol} (7d distributions)` : symbol;

    return {
        value: (
            <Text color="success" variant="body2">
                +{formatPercent(apy)}
            </Text>
        ),
        caption: (
            <CaptionWithIcon
                variant="body2"
                caption={caption}
                icon={<TokenImage metadata={schedule.rewardMetadata} size="sm" />}
            />
        )
    };
}

export function getCollateralYieldStat(
    collateralYield: number | undefined,
    metadata: TokenListMetadata | undefined,
    textColorMap: ReturnType<typeof useTextColorMap>
): StatProps[] {
    if (!collateralYield || collateralYield === 0 || !metadata) return [];
    const symbol = BsMetaUtil.getSymbol(metadata);
    return [
        {
            value: <Text variant="body2">{formatPercent(collateralYield)}</Text>,
            caption: (
                <CaptionWithIcon
                    variant="body2"
                    caption={`${symbol} APY`}
                    iconColor={textColorMap("caption")}
                    icon={<LayersOutlined />}
                />
            )
        }
    ];
}
